import { abortError, throwIfAborted } from './pollUntil.js';
export class UploadStepError extends Error {
    constructor(message, step, previousResult) {
        super(message);
        this.step = step;
        this.previousResult = previousResult;
        this.uploadStep = step;
    }
}
async function atStep(step, fn, previousResult) {
    try {
        return await fn();
    }
    catch (cause) {
        if (cause instanceof Error && cause.name === 'AbortError')
            throw cause;
        throw new UploadStepError(cause instanceof Error ? cause.message : String(cause), step, previousResult);
    }
}
export function putPresignedFile(file, put, signal, onProgress) {
    return new Promise((resolve, reject) => {
        if (signal.aborted)
            return reject(abortError());
        const xhr = new XMLHttpRequest();
        let settled = false;
        let lastProgress = 0;
        const settle = (error) => {
            if (settled)
                return;
            settled = true;
            signal.removeEventListener('abort', abort);
            if (error)
                reject(error);
            else
                resolve();
        };
        const abort = () => {
            xhr.abort();
            settle(abortError());
        };
        xhr.open('PUT', put.url);
        xhr.setRequestHeader('Content-Type', put.contentType);
        xhr.upload.onprogress = (event) => {
            if (event.lengthComputable && event.total > 0) {
                lastProgress = Math.max(lastProgress, Math.max(0, Math.min(1, event.loaded / event.total)));
                onProgress(lastProgress);
            }
        };
        xhr.onload = () => xhr.status >= 200 && xhr.status < 300 ? settle() : settle(new Error(`Upload failed (${xhr.status})`));
        xhr.onerror = () => settle(new Error('Upload failed'));
        xhr.onabort = () => settle(abortError());
        signal.addEventListener('abort', abort, { once: true });
        xhr.send(file);
    });
}
export function presignedS3Uploader({ presign, finalize, awaitProcessed, }) {
    return async (file, { signal, onProgress, onProcessing, previousResult }) => {
        throwIfAborted(signal);
        // A finalized row survives a processing failure. Retry only that step.
        if (previousResult !== undefined && awaitProcessed) {
            onProgress(1);
            onProcessing();
            return atStep('processing', () => awaitProcessed(previousResult, signal), previousResult);
        }
        const signed = await atStep('signed_url', () => presign(file, signal));
        throwIfAborted(signal);
        await atStep('s3_put', () => putPresignedFile(file, signed, signal, onProgress));
        throwIfAborted(signal);
        onProgress(1);
        if (!finalize)
            return signed;
        if (awaitProcessed)
            onProcessing();
        const result = await atStep('create', () => finalize(file, signed, signal));
        throwIfAborted(signal);
        if (!awaitProcessed)
            return result;
        return atStep('processing', () => awaitProcessed(result, signal), result);
    };
}
//# sourceMappingURL=presignedS3Uploader.js.map